/**
 * Google Sheets Integration Utilities
 * Fetches and parses CSV data from a published Google Sheet
 */

export interface GoogleSheetsConfig {
  sheetUrl: string
  refreshInterval?: number // in milliseconds
}

/**
 * Fetches CSV data from a published Google Sheet
 */
export async function fetchGoogleSheetData(sheetUrl: string): Promise<string> {
  try {
    const response = await fetch(sheetUrl)
    if (!response.ok) {
      throw new Error(`Failed to fetch sheet: ${response.statusText}`)
    }
    return await response.text()
  } catch (error) {
    console.error('Error fetching Google Sheet:', error)
    throw error
  }
}

/**
 * Parses CSV text into an array of objects
 * Preserves DD/MM/YYYY date format from Google Sheets
 */
export function parseCSV<T>(csvText: string): T[] {
  const lines = csvText.trim().split('\n')
  if (lines.length < 2) return []

  // Get headers from first row
  const headers = lines[0].split(',').map((h) => h.trim())

  // Parse data rows
  const data: T[] = []
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i])
    if (values.length === headers.length) {
      const row: any = {}
      headers.forEach((header, index) => {
        let value = values[index].trim()
        // Keep dates in DD/MM/YYYY format as-is from Google Sheets
        row[header] = value
      })
      data.push(row as T)
    }
  }

  return data
}

/**
 * Parses a single CSV line, handling quoted values with commas
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]

    if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === ',' && !inQuotes) {
      result.push(current)
      current = ''
    } else {
      current += char
    }
  }

  result.push(current)
  return result
}

/**
 * Fetches and parses Google Sheet data in one call
 */
export async function getGoogleSheetData<T>(sheetUrl: string): Promise<T[]> {
  const csvText = await fetchGoogleSheetData(sheetUrl)
  return parseCSV<T>(csvText)
}
